import { useEffect, useMemo } from 'react'
import type { Goal, Task, SubTask, TimelineDate } from '../../types/timeline'
import { DiffField, DiffFieldReadonly } from './DiffField'
import { toSafeDateInputValue, toSafeIso } from '../../utils/safeDate'
import { ColorPicker, COLOR_PICKER_SWATCHES, DatePicker, TextInput, Textarea } from '../Common'
import { GoalClientPicker } from '../DrawingBoard/GoalClientPicker'
import { useWorkspace } from '../../context/useWorkspace'

type GoalLike = Goal | Task | SubTask

const isGoal = (item: GoalLike | null): item is Goal => Boolean(item && 'tasks' in item)

const toInputDate = (value?: TimelineDate): string => toSafeDateInputValue(value)

const minIso = (...vals: (string | null | undefined)[]): string | undefined => {
  const cleaned = vals.filter((v): v is string => Boolean(v))
  if (cleaned.length === 0) return undefined
  return cleaned.reduce((a, b) => (a < b ? a : b))
}

const maxIso = (...vals: (string | null | undefined)[]): string | undefined => {
  const cleaned = vals.filter((v): v is string => Boolean(v))
  if (cleaned.length === 0) return undefined
  return cleaned.reduce((a, b) => (a > b ? a : b))
}

const formatDateDisplay = (value?: TimelineDate): string => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString()
}

const getColor = (item: GoalLike): string =>
  (isGoal(item) ? item.baseColor : item.color) || COLOR_PICKER_SWATCHES[0]

const setColor = (item: GoalLike, color: `#${string}`): GoalLike =>
  isGoal(item) ? { ...item, baseColor: color } : { ...item, color }

interface GoalLikeFormProps {
  /** The editable (working / proposed) version. */
  item: GoalLike | null
  /** Edit-mode baseline. Null in generate/detail or when this node is new. */
  baseline: GoalLike | null
  /**
   * Apply a patch to the working item. Caller wires this back into the goal
   * tree at the right level.
   */
  onChange: (next: GoalLike) => void
  /** When true (e.g. edit-mode middle column / detail mode initial), disable inputs. */
  readOnly: boolean
  /** Field names that failed post-flight or live validation. Drives the
   * yellow ⚠️ border + tooltip on the offending input. */
  invalidFields?: Set<string>
  /** Immediate parent's start; null/undefined means no lower parent bound. */
  parentStart?: TimelineDate | null
  /** Immediate parent's end; null/undefined means no upper parent bound. */
  parentEnd?: TimelineDate | null
}

/**
 * Manage-screen-matching form for Goal/Task/Subtask: name, description,
 * start/end dates, colour. Used for the proposed (new) column AND, when
 * `readOnly` is true and `baseline` is set, for the baseline column.
 */
export const GoalLikeForm = ({
  item,
  baseline,
  onChange,
  readOnly,
  invalidFields,
  parentStart,
  parentEnd,
}: GoalLikeFormProps) => {
  let activeWorkspaceId: string | null = null
  try {
    const ws = useWorkspace()
    activeWorkspaceId = ws.activeWorkspaceId ?? null
  } catch {
    // Fail open when rendered outside WorkspaceProvider
  }
  const isInvalid = (field: string) => Boolean(invalidFields?.has(field))
  // Stable computed values used by DiffField to detect changes.
  const baselineColor = useMemo(() => (baseline ? getColor(baseline) : undefined), [baseline])

  const workingItemId = item?.id

  useEffect(() => {
    let rafId: number | null = null
    const handleFocusField = (event: Event) => {
      const customEvent = event as CustomEvent<{ nodeId?: string; field?: string }>
      const detail = customEvent.detail
      if (!detail || !workingItemId || detail.nodeId !== workingItemId) return

      if (rafId != null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const selector = detail.field
          ? `[data-field="${detail.field}"][data-node-id="${workingItemId}"]`
          : `[data-node-id="${workingItemId}"]`
        const target = document.querySelector<HTMLElement>(selector)
        if (target) {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' })
          const innerInput = target.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            'input, textarea, select, [contenteditable="true"]',
          ) ?? (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target : null)
          innerInput?.focus()
        }
      })
    }

    window.addEventListener('detailedView:focusField', handleFocusField)
    return () => {
      window.removeEventListener('detailedView:focusField', handleFocusField)
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [workingItemId])

  if (!item && !baseline) {
    return <div className="detailedFormEmpty">Pick an item from the tree.</div>
  }

  if (!item && baseline) {
    // Removed-in-edit case: the AI dropped this node. Show baseline read-only
    // so the user can see what disappeared.
    return (
      <div className="detailedForm detailedForm--removed">
        <p className="detailedFormBanner detailedFormBanner--removed">This item was removed by the AI edit.</p>
        <DiffFieldReadonly label="Name" display={baseline.title || <em>—</em>} />
        <DiffFieldReadonly label="Description" display={baseline.description || <em>No description</em>} />
        <DiffFieldReadonly label="Start date" display={formatDateDisplay(baseline.startDate)} />
        <DiffFieldReadonly label="End date" display={formatDateDisplay(baseline.endDate)} />
        <DiffFieldReadonly
          label="Colour"
          display={<span className="detailedFormColorSwatch" style={{ background: getColor(baseline) }} aria-label={getColor(baseline)} />}
        />
      </div>
    )
  }

  if (readOnly && item) {
    // Baseline column in edit mode: same fields rendered as static text.
    return (
      <div className="detailedForm detailedForm--readonly">
        <DiffFieldReadonly label="Name" display={item.title || <em>—</em>} />
        <DiffFieldReadonly label="Description" display={item.description || <em>No description</em>} />
        <DiffFieldReadonly label="Start date" display={formatDateDisplay(item.startDate)} />
        <DiffFieldReadonly label="End date" display={formatDateDisplay(item.endDate)} />
        <DiffFieldReadonly
          label="Colour"
          display={<span className="detailedFormColorSwatch" style={{ background: getColor(item) }} aria-label={getColor(item)} />}
        />
      </div>
    )
  }

  // Editable working column.
  const workingItem = item!
  const color = getColor(workingItem)

  return (
    <div className="detailedForm detailedForm--v2">
      <section className="detailedFormCard detailedFormCard--overview" aria-label="Overview">
        <header className="detailedFormCard__header">
          <span className="detailedFormCard__eyebrow">Overview</span>
        </header>
        <div className="detailedFormCard__body">
          <DiffField
            label="Name"
            dataField="title"
            dataNodeId={workingItem.id}
            value={workingItem.title}
            previousValue={baseline?.title}
            isInvalid={isInvalid('title')}
            invalidReason={isInvalid('title') ? 'Title is empty or looks invalid.' : undefined}
          >
            <TextInput
              value={workingItem.title}
              onChange={(title) => onChange({ ...workingItem, title })}
            />
          </DiffField>

          <DiffField label="Description" value={workingItem.description} previousValue={baseline?.description}>
            <Textarea
              rows={6}
              value={workingItem.description}
              onChange={(description) => onChange({ ...workingItem, description })}
            />
          </DiffField>

          {isGoal(workingItem) && (
            <GoalClientPicker
              workspaceId={activeWorkspaceId ?? ''}
              clientId={(workingItem as Goal).clientId}
              onChange={(clientId) => onChange({ ...workingItem, clientId } as GoalLike)}
              disabled={readOnly}
              className="detailedFormClientRow"
            />
          )}
        </div>
      </section>

      <section className="detailedFormCard detailedFormCard--schedule" aria-label="Schedule">
        <header className="detailedFormCard__header">
          <span className="detailedFormCard__eyebrow">Schedule</span>
        </header>
        <div className="detailedFormCard__body detailedFormCard__body--schedule">
          <DiffField
            label="Start date"
            dataField="startDate"
            dataNodeId={workingItem.id}
            value={toInputDate(workingItem.startDate)}
            previousValue={baseline ? toInputDate(baseline.startDate) : undefined}
            isInvalid={isInvalid('startDate')}
            invalidReason={isInvalid('startDate') ? 'Start date is missing, out of range, or after the end date.' : undefined}
          >
            <DatePicker
              value={toInputDate(workingItem.startDate)}
              ariaLabel="Start date"
              min={toInputDate(parentStart ?? undefined) || undefined}
              max={minIso(toInputDate(parentEnd ?? undefined) || undefined, toInputDate(workingItem.endDate) || undefined)}
              onChange={(raw) =>
                onChange({
                  ...workingItem,
                  // Clearing yields an empty string; new Date('').toISOString() throws.
                  // Route through toSafeIso so we degrade to today's date instead.
                  startDate: toSafeIso(raw, toSafeIso(workingItem.startDate)),
                })
              }
            />
          </DiffField>

          <DiffField
            label="End date"
            dataField="endDate"
            dataNodeId={workingItem.id}
            value={toInputDate(workingItem.endDate)}
            previousValue={baseline ? toInputDate(baseline.endDate) : undefined}
            isInvalid={isInvalid('endDate')}
            invalidReason={isInvalid('endDate') ? 'End date is missing, out of range, or before the start date.' : undefined}
          >
            <DatePicker
              value={toInputDate(workingItem.endDate)}
              ariaLabel="End date"
              min={maxIso(toInputDate(parentStart ?? undefined) || undefined, toInputDate(workingItem.startDate) || undefined)}
              max={toInputDate(parentEnd ?? undefined) || undefined}
              onChange={(raw) =>
                onChange({
                  ...workingItem,
                  endDate: toSafeIso(raw, toSafeIso(workingItem.endDate)),
                })
              }
            />
          </DiffField>

          <DiffField label="Colour" value={color} previousValue={baselineColor}>
            <div className="detailedFormColorRow">
              <ColorPicker
                value={color}
                onChange={(hex) => onChange(setColor(workingItem, hex as `#${string}`))}
                ariaLabel="Colour"
              />
              <code>{color}</code>
            </div>
          </DiffField>
        </div>
      </section>
    </div>
  )
}
